import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useQuery } from "@apollo/client/react";
import { MY_PRODUCTS_QUERY } from "../graphql/myProducts";
import { MY_DOWNLOADS_QUERY } from "../graphql/myDownloads";
import { MY_REPOS_QUERY } from "../graphql/myRepos";
import "./portal.css";
import { initialState, routeMeta, AppState, Route } from "./state";
import { Sidebar, Topbar, TicketModal, Toast } from "./components";
import {
  DashboardView,
  ProductsView,
  DownloadsView,
  ReposView,
  DocsView,
  SupportView,
  AccountView,
} from "./views";
import { AdminDashboard } from "./admin/AdminDashboard";

function PortalApp(): React.ReactElement {
  const [state, setState] = useState<AppState>(initialState);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [isTicketModalOpen, setTicketModalOpen] = useState<boolean>(false);

  // Get the authenticated portal user's ID
const storedUser = localStorage.getItem("user");

let portalUserId: number | null = null;

try {
  const user = storedUser ? JSON.parse(storedUser) : null;
  const id = Number(user?.id);

  if (Number.isSafeInteger(id) && id > 0) {
    portalUserId = id;
  }
} catch {
  portalUserId = null;
}

const searchVariables = {
  portalUserId: portalUserId ?? 0,
};

const { data: productsData } = useQuery<any>(MY_PRODUCTS_QUERY, {
  variables: searchVariables,
  skip: portalUserId === null,
});

const { data: downloadsData } = useQuery<any>(MY_DOWNLOADS_QUERY, {
  variables: searchVariables,
  skip: portalUserId === null,
});

const { data: reposData } = useQuery<any>(MY_REPOS_QUERY, {
  variables: searchVariables,
  skip: portalUserId === null,
});

// Normalize the different GraphQL records into searchable results
const searchResults = useMemo(() => {
  const query = state.globalQuery.trim().toLowerCase();

  if (!query) return [];

  const results: {
    id: string;
    title: string;
    subtitle: string;
    route: string;
  }[] = [];

  const addResult = (
    id: string,
    title: string,
    subtitle: string,
    route: string,
    searchableFields: unknown[]
  ) => {
    const matches = searchableFields.some((value) =>
      String(value ?? "").toLowerCase().includes(query)
    );

    if (matches) {
      results.push({ id, title, subtitle, route });
    }
  };

  for (const product of productsData?.myProducts ?? []) {
    addResult(
      `product-${product.id}`,
      product.name || "Unnamed product",
      `Product • ${product.selected_version || "No version"}`,
      "products",
      [
        product.name,
        product.description,
        product.model_num,
        product.selected_version,
        product.arch,
      ]
    );
  }

  for (const iso of downloadsData?.myIsos ?? []) {
    addResult(
      `iso-${iso.id}`,
      iso.label || iso.product_name || "ISO Download",
      `ISO • ${iso.version || "No version"} • ${iso.arch || ""}`,
      "downloads",
      [
        iso.label,
        iso.product_name,
        iso.model_num,
        iso.version,
        iso.arch,
        iso.path,
      ]
    );
  }

  for (const repo of reposData?.myRepos ?? []) {
    addResult(
      `repo-${repo.id}`,
      repo.product_name || "Repository",
      `Repository • ${repo.version || "No version"} • ${repo.arch || ""}`,
      "repos",
      [
        repo.product_name,
        repo.model_num,
        repo.version,
        repo.arch,
        repo.repo_path,
      ]
    );
  }

  return results.slice(0, 30);
}, [
  state.globalQuery,
  productsData,
  downloadsData,
  reposData,
]);

  const showToast = useCallback((msg: string): void => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 1600);
  }, []);

  const setRoute = (route: string): void => {
    setState((prev) => ({ ...prev, route: route as Route }));
  };

  const meta = useMemo(
    () => routeMeta(state.route, state.role, () => setTicketModalOpen(true)),
    [state.route, state.role]
  );

  const onGlobalSearchChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const value = e.target.value;
    setState((prev) => ({ ...prev, globalQuery: value }));
  };

  const toggleRole = (): void => {
    setState((prev) => {
      const role = prev.role === "Org Admin" ? "Standard User" : "Org Admin";
      showToast(`Role switched to ${role} (mock).`);
      return { ...prev, role };
    });
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        const el = document.querySelector<HTMLInputElement>(".search input");
        if (el) el.focus();
      }

      if (e.key === "Escape") {
        setTicketModalOpen(false);
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    const openModal = (): void => setTicketModalOpen(true);

    window.addEventListener("open-ticket-modal", openModal);
    return () => window.removeEventListener("open-ticket-modal", openModal);
  }, []);

  let view: React.ReactElement | null = null;

  if (state.route === "dashboard") {
    view = <DashboardView state={state} setRoute={setRoute} />;
  } else if (state.route === "products") {
    view = <ProductsView state={state} setRoute={setRoute} />;
  } else if (state.route === "downloads") {
    view = <DownloadsView state={state} setState={setState} />;
  } else if (state.route === "repos") {
    view = <ReposView state={state} setState={setState} />;  
  } else if (state.route === "docs") {
    view = <DocsView state={state} setState={setState} />;
  } else if (state.route === "support") {
    view = <SupportView state={state} setState={setState} />;
  } else if (state.route === "account") {
    view = <AccountView state={state} />;
  } else if (state.route === "admin") {
  view = <AdminDashboard />;
}
  return (
    <>
      <div className="app">
        <Sidebar state={state} setRoute={setRoute} toggleRole={toggleRole} />

        <main className="main">
        <Topbar
  state={state}
  meta={meta}
  onGlobalSearchChange={onGlobalSearchChange}
  setRoute={setRoute}
  searchResults={searchResults}
  openTicketModal={() => setTicketModalOpen(true)}
/>

          <section className="content" tabIndex={0}>
            {view}
          </section>
        </main>
      </div>

      {isTicketModalOpen && (
        <TicketModal
          state={state}
          setState={setState}
          close={() => setTicketModalOpen(false)}
        />
      )}

      <Toast message={toastMessage} />
    </>
  );
}

export default PortalApp;